import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Plus, X, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import ComingSoon from "../components/ComingSoon";
import WaitlistTour, { hasSeenWaitlistTour } from "../components/WaitlistTour";
import SmsHealthBanner from "../components/SmsHealthBanner";
import HostAlertsToggle from "../components/HostAlertsToggle";
import FutureInterestCard from "../components/FutureInterestCard";
import OfflineBanner from "../components/OfflineBanner";
import TableGrid from "../components/TableGrid";
import RoiCard from "../components/RoiCard";

// Cache the last-known guest list so the host stand can still see the queue when
// the restaurant's internet is down. Scoped per-business so multi-location switches don't collide.
const cacheKey = (bid) => `buzzerai.waitlist.cache.${bid || "default"}`;
const readCache = (bid) => {
  try {
    const raw = localStorage.getItem(cacheKey(bid));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const writeCache = (bid, guests) => {
  try {
    localStorage.setItem(cacheKey(bid), JSON.stringify({ guests, at: Date.now() }));
  } catch (e) {
    // Safari private mode or storage full — ignore.
    console.debug("waitlist cache write failed:", e?.message);
  }
};

const StatusTag = ({ s }) => {
  const cls = { waiting: "tag-waiting", notified: "tag-notified", seated: "tag-seated", no_show: "tag-noshow", cancelled: "tag-cancelled" }[s] || "tag";
  return <span className={`tag ${cls}`}>{s.replace("_", " ")}</span>;
};

// Format an ISO desired_eat_time down to just the target time-of-day, e.g. "8:00 PM".
const formatEatTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch (e) { return null; }
};

const minutesAgo = (iso) => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return diff <= 0 ? "just now" : `${diff}m ago`;
};

export default function DashWaitlist() {
  const { business, refresh } = useAuth();
  const ent = useEntitlements();
  const [guests, setGuests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", party_size: 2, notes: "", source: "walkin" });
  const [tourOpen, setTourOpen] = useState(false);
  const [offline, setOffline] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  useEffect(() => {
    if (!hasSeenWaitlistTour()) setTourOpen(true);
    // Hydrate from cache immediately so the host stand is never blank on cold load.
    const cached = readCache(business?.id);
    if (cached && Array.isArray(cached.guests)) {
      setGuests(cached.guests);
      setLastSyncedAt(cached.at || null);
    }
    // Track browser online/offline events for instant feedback.
    const goOnline = () => { setOffline(false); load(); };
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (typeof navigator !== "undefined" && navigator.onLine === false) setOffline(true);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/guests");
      setGuests(r.data);
      setOffline(false);
      const stamp = Date.now();
      setLastSyncedAt(stamp);
      writeCache(business?.id, r.data);
    } catch (err) {
      // Network error / server unreachable — flip to offline mode, keep showing cached list.
      const isNetworkErr = !err.response || err.code === "ERR_NETWORK" || err.message === "Network Error";
      if (isNetworkErr) {
        setOffline(true);
      } else {
        toast.error("Load failed");
      }
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const addGuest = async (e) => {
    e.preventDefault();
    try { await api.post("/guests", form); setOpen(false); setForm({ name: "", phone: "", party_size: 2, notes: "", source: "walkin" }); load(); toast.success("Guest added"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const notify = async (id) => {
    try { const r = await api.post(`/guests/${id}/notify`, {}); toast.success(r.data.sms.mocked ? "SMS logged (Twilio not configured)" : "Text sent!"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/guests/${id}`, { status }); load(); } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/guests/${id}`); load(); };

  const active = guests.filter((g) => ["waiting", "notified"].includes(g.status));
  const done = guests.filter((g) => ["seated", "no_show", "cancelled"].includes(g.status));
  // Split SmartAdd auto-promoted guests into their own section so the operator
  // sees at a glance "these came from the AI's SmartAdd flow for a target time"
  // instead of losing that context in the main queue.
  const smartAdd = active.filter((g) => g.smart_add_id || /SmartAdd/i.test(g.notes || ""));
  const regular = active.filter((g) => !smartAdd.includes(g));

  const renderGuestRow = (g, i, opts = {}) => {
    const eatLabel = g.smart_add_target_label || formatEatTime(g.desired_eat_time);
    const isSmart = opts.smart;
    return (
      <div key={g.id} className="flex flex-col sm:grid sm:grid-cols-12 sm:items-center gap-2 sm:gap-0 px-4 sm:px-6 py-4 border-b border-[#E1DFD5] last:border-0 fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`guest-row-${g.id}`}>
        <div className="sm:col-span-4">
          <div className="font-medium flex items-center gap-2 flex-wrap">
            {g.name}
            {g.is_returning && <span className="tag tag-notified inline-flex items-center gap-1" title={`Visit #${g.visits}`}>★ #{g.visits}</span>}
            {isSmart && eatLabel && (
              <span className="tag inline-flex items-center gap-1 bg-[#FEF3C7] text-[#92400E]" title="Target eat time from Wait Watch" data-testid={`smart-target-${g.id}`}>
                <Sparkle size={11} weight="fill" color="#92400E" /> target {eatLabel}
              </span>
            )}
          </div>
          <div className="text-xs text-[#5C6B61]">{g.phone || "walk-in"} · party {g.party_size} · {minutesAgo(g.created_at)} · {g.notes || g.source}</div>
        </div>
        <div className="hidden sm:block sm:col-span-2">{g.party_size}</div>
        <div className="hidden sm:block sm:col-span-2 text-[#5C6B61]">{minutesAgo(g.created_at)}</div>
        <div className="sm:col-span-2 flex sm:block"><StatusTag s={g.status} /></div>
        <div className="sm:col-span-2 flex sm:justify-end gap-2 flex-wrap">
          {g.status === "waiting" && (
            <button data-testid={`notify-${g.id}`} onClick={() => notify(g.id)} className="btn-ghost !py-1.5 !px-3 text-xs"><PaperPlaneTilt size={14} /> Text ready</button>
          )}
          <button data-testid={`seat-${g.id}`} onClick={() => setStatus(g.id, "seated")} className="btn-ghost !py-1.5 !px-3 text-xs"><CheckCircle size={14} /> Seat</button>
          <button data-testid={`remove-${g.id}`} onClick={() => remove(g.id)} className="w-8 h-8 rounded-full hover:bg-[#F1D7D2]"><X size={14} className="mx-auto" color="#A5382A" /></button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <WaitlistTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <OfflineBanner offline={offline} lastSyncedAt={lastSyncedAt} onRetry={load} />
      <SmsHealthBanner />

      {/* Waitlist master switch — the host runs this with their eyes on the
          room. Default = closed. Tap when the last table fills up. Tap again
          to pause once tables free up. Uses the existing
          waitlist_manual_status field (open|closed) — nothing new backend-side. */}
      {(() => {
        const status = business.waitlist_manual_status || "closed";
        const isOpen = status === "open" || status === "auto";
        const toggle = async () => {
          try {
            const next = isOpen ? "closed" : "open";
            await api.patch("/settings", { waitlist_manual_status: next });
            toast.success(next === "open" ? "Waitlist is now OPEN — guests can join at /join." : "Waitlist paused. Walk-in only.");
            refresh && refresh();
          } catch { toast.error("Toggle failed"); }
        };
        return (
          <div className={`paper-card p-4 mb-6 flex items-center gap-4 border-l-4 ${isOpen ? "border-[#D96C5B] bg-[#F1D7D2]/25" : "border-[#1E3A2F] bg-[#C2D1C1]/25"}`} data-testid="waitlist-master-switch">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isOpen ? "bg-[#D96C5B]/25" : "bg-[#1E3A2F]/15"}`}>
              <span className={`w-3 h-3 rounded-full ${isOpen ? "bg-[#D96C5B] animate-pulse" : "bg-[#1E3A2F]"}`} />
            </div>
            <div className="flex-1">
              <div className="overline">{isOpen ? "Waitlist is OPEN" : "Waitlist is paused"}</div>
              <p className="text-sm text-[#5C6B61] leading-snug">
                {isOpen
                  ? "Guests can join the queue at /join and Mimi is adding callers. Pause this when tables free up."
                  : "Walk-in only. The public /join page and Mimi tell guests you're seating immediately. Start this when the room fills up."}
              </p>
            </div>
            <button
              type="button"
              onClick={toggle}
              className={isOpen ? "btn-ghost !py-2 !px-4 text-sm" : "btn-primary !py-2 !px-4 text-sm"}
              data-testid="waitlist-toggle"
            >
              {isOpen ? "Pause waitlist" : "Start waitlist"}
            </button>
          </div>
        );
      })()}

      <div className="mb-6"><HostAlertsToggle /></div>
      <FutureInterestCard />
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline">Live queue</div>
          <h1 className="text-3xl">Waitlist</h1>
          <p className="text-[#5C6B61] mt-1">{active.length} guests waiting · avg wait ~{business.average_wait_minutes}m</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className="btn-ghost !py-2 !px-3 text-sm inline-flex items-center gap-1.5"
            data-testid="waitlist-tour-launch"
          >
            <Sparkle size={14} color="#1E3A2F" weight="fill" /> Take a tour
          </button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary" data-testid="add-guest-btn"><Plus size={16} color="#fff" />Add guest</button>
          </DialogTrigger>
          <DialogContent className="bg-white border border-[#E1DFD5]">
            <DialogHeader><DialogTitle>Add guest (walk-in or phone)</DialogTitle></DialogHeader>
            <form onSubmit={addGuest} className="space-y-3 pt-2">
              <input required className="input-field" placeholder="Guest name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-name" />
              <input className="input-field" placeholder="Mobile phone (optional but recommended)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-phone" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="number" min={1} className="input-field" placeholder="Party size" value={form.party_size} onChange={(e) => setForm({ ...form, party_size: parseInt(e.target.value || "1") })} data-testid="add-party" />
                <select className="input-field" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="add-source">
                  <option value="walkin">Walk-in</option>
                  <option value="phone">Phone call</option>
                  <option value="web">Web/QR</option>
                </select>
              </div>
              <input className="input-field" placeholder="Notes (booth, high chair, allergies)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="add-notes" />
              <p className="text-[11px] text-[#5C6B61] leading-snug">
                {form.phone ? "You confirm the guest agreed to receive text updates about their spot." : "No phone entered — the guest won't get an SMS when their table is ready. Ask them to stay nearby."}
              </p>
              <button className="btn-primary w-full justify-center" data-testid="add-submit">Add to waitlist</button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <RoiCard />

      <TableGrid business={business} waitingGuests={active.filter((g) => g.status === "waiting" || g.status === "notified")} onChange={refresh} />

      {smartAdd.length > 0 && false && (
        <div className="paper-card overflow-hidden mb-6 border-l-4 border-[#D96C5B]" data-testid="smartadd-guests-section">
          <div className="px-6 py-3 border-b border-[#E1DFD5] bg-[#FEF3C7]/40">
            <div className="overline flex items-center gap-2 text-[#92400E]"><Sparkle size={12} weight="fill" color="#D96C5B" /> Wait Watch · auto-added for target eat time</div>
            <div className="text-xs text-[#5C6B61] mt-0.5">{smartAdd.length} guest{smartAdd.length === 1 ? "" : "s"} the AI put on the list to line up with their target eat time. Seat them like anyone else once their table is ready.</div>
          </div>
          <div className="hidden sm:grid grid-cols-12 px-6 py-3 border-b border-[#E1DFD5] overline">
            <div className="col-span-4">Guest</div><div className="col-span-2">Party</div><div className="col-span-2">Waiting</div><div className="col-span-2">Status</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {smartAdd.map((g, i) => renderGuestRow(g, i, { smart: true }))}
        </div>
      )}

      <div className="paper-card overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 px-6 py-3 border-b border-[#E1DFD5] overline">
          <div className="col-span-4">Guest</div><div className="col-span-2">Party</div><div className="col-span-2">Waiting</div><div className="col-span-2">Status</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {regular.length === 0 && smartAdd.length === 0 && <div className="p-10 text-center text-[#5C6B61]">No one on the list. Enjoy the calm.</div>}
        {regular.length === 0 && smartAdd.length > 0 && <div className="p-10 text-center text-[#5C6B61]">No one on the list. Enjoy the calm.</div>}
        {regular.map((g, i) => renderGuestRow(g, i))}
      </div>

      {done.length > 0 && (
        <div className="mt-10">
          <div className="overline mb-3">Recently closed</div>
          <div className="paper-card divide-y divide-[#E1DFD5]">
            {done.slice(0, 10).map((g) => (
              <div key={g.id} className="px-6 py-3 flex items-center justify-between text-sm">
                <div className="text-[#5C6B61]">{g.name} · party {g.party_size} · {minutesAgo(g.created_at)}</div>
                <StatusTag s={g.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      <ComingSoon compact />
    </div>
  );
}
